/**
 * PageHeader — sticky glass header used across all main pages
 * (Dashboard, Reports, Payments, Receivables, History, Accounts, …).
 *
 * Visual rules from /app/design_guidelines.json:
 *   • Backdrop blur 14px, white at 78% opacity.
 *   • 1px bottom border in slate-200.
 *   • Sticky on the viewport top.
 *   • Title uses font-display, body-side actions float right.
 *
 * iter-196f — mobile sticky offset fix. MobileTopBar above is
 *   ``sticky top-0 z-[60] h-14`` in the same scroll container, so we
 *   pin at ``top-14`` on mobile (slides below the topbar) and
 *   ``lg:top-0`` on desktop (where MobileTopBar is hidden).
 *
 * iter-196g — optional ``resourceKey`` prop. When set, renders the
 *   CapUsageBadge inline next to the title (with the resource label
 *   visible) so the user reads "Metas · 4/30 metas" at a glance. The
 *   matching CapAwareButton on the page should pass ``hideBadge`` so
 *   the chip doesn't appear twice.
 */
import { CapUsageBadge } from "./CapUsageBadge";
import { useLang } from "../contexts/LangContext";

export function PageHeader({
  eyebrow,
  title,
  subtitle,
  actions,
  sticky = true,
  testid,
  resourceKey,
}) {
  const { lang } = useLang();
  const isEs = lang !== "en";
  return (
    <div
      data-testid={testid || "page-header"}
      className={`${sticky ? "sticky top-14 lg:top-0 z-20" : ""} lf-glass-header px-4 sm:px-6 lg:px-8 py-2.5 sm:py-4 -mx-4 sm:-mx-6 lg:-mx-8 mt-0 sm:-mt-6 lg:-mt-8 mb-3 sm:mb-6`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          {eyebrow && (
            <div className="lf-label mb-1" data-testid="page-header-eyebrow">{eyebrow}</div>
          )}
          <h1
            className="font-display text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100 leading-tight tracking-tight flex items-center gap-2 flex-wrap"
            data-testid="page-header-title"
          >
            <span>{title}</span>
            {resourceKey && (
              <span className="inline-flex items-baseline self-center" data-testid="page-header-cap-badge">
                <CapUsageBadge resourceKey={resourceKey} isEs={isEs} showLabel />
              </span>
            )}
          </h1>
          {subtitle && (
            <p className="text-sm text-slate-500 mt-1 truncate" data-testid="page-header-subtitle">
              {subtitle}
            </p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-2 flex-shrink-0 mt-1" data-testid="page-header-actions">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}
